import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './TryGeneratePage.scss';

interface MockGenerationSuggestions {
  topics: string[];
  difficulties: Array<{
    value: string;
    label: string;
    description: string;
    duration: string;
  }>;
  focusAreas: string[];
}

export const TryGeneratePage: React.FC = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    topic: '',
    difficulty: 'basic' as 'basic' | 'intermediate' | 'advanced',
    focusAreas: [] as string[],
  });

  // Mock suggestions data
  const suggestions: MockGenerationSuggestions = {
    topics: [
      'React and Modern Frontend Development',
      'Python for Data Science',
      'Machine Learning Fundamentals',
      'Cloud Computing with AWS',
      'Digital Marketing Strategies',
    ],
    difficulties: [
      {
        value: 'basic',
        label: 'Beginner',
        description: 'Perfect for those new to the subject',
        duration: '6-12 lessons, 45-60 min each',
      },
      {
        value: 'intermediate',
        label: 'Intermediate',
        description: 'For those with some foundational knowledge',
        duration: '15-25 lessons, 90-120 min each',
      },
      {
        value: 'advanced',
        label: 'Advanced',
        description: 'Deep, comprehensive coverage for experts',
        duration: '25+ lessons, 120-180 min each',
      },
    ],
    focusAreas: [
      'Practical Applications',
      'Theory and Concepts',
      'Hands-on Projects',
      'Case Studies',
      'Industry Best Practices',
      'Tools and Technologies',
      'Problem Solving',
      'Critical Thinking',
    ],
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Redirect to pricing page instead of actually generating
    navigate('/pricing');
  };

  const handleFocusAreaToggle = (area: string) => {
    setFormData(prev => ({
      ...prev,
      focusAreas: prev.focusAreas?.includes(area)
        ? prev.focusAreas.filter(a => a !== area)
        : [...(prev.focusAreas || []), area],
    }));
  };

  return (
    <div className="generate-page">
      <div className="container">
        <div className="generate-page-header">
          <h1>Try Course Generation</h1>
          <p>
            Experience how Odyssey creates personalized courses tailored to your
            learning goals
          </p>
        </div>

        <div className="form-card">
          <form onSubmit={handleSubmit} className="form-container">
            {/* Topic */}
            <div className="form-group">
              <label className="form-label">
                Course Topic <span className="required">*</span>
              </label>
              <input
                type="text"
                value={formData.topic}
                onChange={e =>
                  setFormData(prev => ({ ...prev, topic: e.target.value }))
                }
                placeholder="e.g., Machine Learning Fundamentals"
                required
                className="form-input"
              />

              <div className="topic-tips">
                <p className="tips-content">
                  Be as specific or general as you'd like.
                </p>
                <div className="tips-examples">
                  <p className="examples-list">
                    Physics, React for e-commerce, 19th century maritime
                    knot-tying for beginners
                  </p>
                </div>
              </div>

              <div className="suggestions-container">
                <p className="suggestions-label">Popular topics:</p>
                <div className="suggestions-grid">
                  {suggestions.topics.slice(0, 5).map(topic => (
                    <button
                      key={topic}
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, topic }))}
                      className="suggestion-button"
                    >
                      {topic}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Difficulty */}
            <div className="form-group">
              <label className="form-label">
                Difficulty Level <span className="required">*</span>
              </label>
              <div className="difficulty-grid">
                {suggestions.difficulties.map(diff => (
                  <div
                    key={diff.value}
                    className={`difficulty-option ${
                      formData.difficulty === diff.value ? 'selected' : ''
                    }`}
                    onClick={() =>
                      setFormData(prev => ({
                        ...prev,
                        difficulty: diff.value as any,
                      }))
                    }
                  >
                    <div className="option-header">
                      <input
                        type="radio"
                        value={diff.value}
                        checked={formData.difficulty === diff.value}
                        onChange={() =>
                          setFormData(prev => ({
                            ...prev,
                            difficulty: diff.value as any,
                          }))
                        }
                        className="option-radio"
                      />
                      <span className="option-title">{diff.label}</span>
                    </div>
                    <p className="option-description">{diff.description}</p>
                    <p className="option-duration">{diff.duration}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Focus Areas */}
            <div className="form-group">
              <label className="form-label">Focus Areas (Optional)</label>
              <div className="focus-areas-grid">
                {suggestions.focusAreas.map(area => (
                  <div
                    key={area}
                    className={`focus-area-option ${
                      formData.focusAreas?.includes(area) ? 'selected' : ''
                    }`}
                    onClick={() => handleFocusAreaToggle(area)}
                  >
                    <div className="focus-area-content">
                      <input
                        type="checkbox"
                        checked={formData.focusAreas?.includes(area) || false}
                        onChange={() => handleFocusAreaToggle(area)}
                        className="focus-area-checkbox"
                      />
                      <span className="focus-area-label">{area}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Submit Button */}
            <div className="form-actions">
              <button
                type="submit"
                disabled={!formData.topic}
                className="submit-button demo-submit"
              >
                Try Generate Course
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};
